import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Badge from '../ui/Badge';
import DriverOrdersTable from './DriverOrdersTable';
import {
  getAdminDriverDetail,
  getDriverAdjustments,
  updateDriverStatus,
  updateDriverCheckrStatus,
  updateDriverQuizStatus,
  updateDriverRating,
  reviewDriverCompliance,
  createDriverAdjustment,
  uploadDriverCompliance,
  fetchDriverStripeOnboardingLink,
} from '../../store/thunks/driverActions';

/**
 * Driver admin detail. Renders:
 *   - Header card (identity, rating, active/inactive toggle, eligibility chips).
 *   - Compliance review panel (photo ID + selfie with approve/reject).
 *   - Adjustments ledger (pending + historical penalties/credits).
 *   - Recent orders table (reuses existing DriverOrdersTable).
 */

function SectionCard({ title, subtitle, children, action }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-[#18181B]">{title}</h3>
          {subtitle && <p className="text-xs text-[#71717A] mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function RatingBig({ value }) {
  const num = Number(value ?? 5).toFixed(1);
  const tone = value < 3 ? 'text-red-600' : value < 4 ? 'text-amber-600' : 'text-emerald-600';
  return (
    <span className={`inline-flex items-center gap-2 font-semibold text-2xl ${tone}`}>
      <svg className="w-6 h-6" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M10 15.27 16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z" />
      </svg>
      {num}
      <span className="text-sm text-[#71717A] font-normal">/ 5.0</span>
    </span>
  );
}

function ComplianceDoc({ label, doc, onReview, onUpload, uploading }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const status = doc?.status || 'missing';
  const variant = {
    approved: 'success',
    pending: 'warning',
    rejected: 'danger',
    missing: 'default',
  }[status];

  return (
    <div className="border border-[#E4E4E7] rounded-lg p-4">
      <div className="flex justify-between items-center mb-3">
        <div>
          <div className="font-semibold text-[#18181B]">{label}</div>
          <div className="text-xs text-[#71717A]">
            {doc?.uploadedAt
              ? `Uploaded ${new Date(doc.uploadedAt).toLocaleString()}`
              : 'No document on file'}
          </div>
        </div>
        <Badge variant={variant}>{status.charAt(0).toUpperCase() + status.slice(1)}</Badge>
      </div>
      <div className="mb-3 flex items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#D4D4D8] px-3 py-1.5 text-xs font-medium text-[#3F3F46] hover:border-[#A5B4FC] hover:text-[#3730A3]">
          <input
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            disabled={uploading}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) onUpload?.(file);
            }}
          />
          {uploading ? 'Uploading…' : doc?.url ? 'Replace file' : 'Upload file'}
        </label>
        <span className="text-[11px] text-[#71717A]">
          Uploading resets this document to pending review.
        </span>
      </div>
      {doc?.url && (
        <a
          href={doc.url}
          target="_blank"
          rel="noreferrer"
          className="block mb-3 rounded-lg overflow-hidden border border-[#E4E4E7] hover:border-[#4F46E5]"
        >
          <img src={doc.url} alt={label} className="w-full max-h-64 object-contain bg-[#FAFAFA]" />
        </a>
      )}
      {doc?.rejectionReason && (
        <div className="text-xs text-[#991B1B] mb-3">Rejected: {doc.rejectionReason}</div>
      )}
      {status === 'pending' && (
        <div className="flex gap-2">
          <button
            className="bg-[#059669] text-white text-xs px-3 py-1.5 rounded-full"
            onClick={() => onReview({ status: 'approved' })}
          >
            Approve
          </button>
          <button
            className="bg-[#DC2626] text-white text-xs px-3 py-1.5 rounded-full"
            onClick={() => setOpen(!open)}
          >
            Reject
          </button>
        </div>
      )}
      {open && status === 'pending' && (
        <div className="mt-3 space-y-2">
          <textarea
            className="w-full px-2 py-1 border rounded text-xs"
            rows={2}
            placeholder="Rejection reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <button
            className="bg-[#DC2626] text-white text-xs px-3 py-1.5 rounded-full disabled:opacity-50"
            disabled={!reason.trim()}
            onClick={() => {
              onReview({ status: 'rejected', rejectionReason: reason.trim() });
              setOpen(false);
              setReason('');
            }}
          >
            Confirm Reject
          </button>
        </div>
      )}
    </div>
  );
}

const DriverPersonalComponent = () => {
  const dispatch = useDispatch();
  const { did } = useParams();
  const { data, loading, adjustments, adjustmentsLoading } = useSelector(
    (state) => state.adminDriverDetail ?? {}
  );
  const driver = data?.driver;
  const stats = data?.stats;
  const recentOrders = data?.recentOrders;

  const [ratingDraft, setRatingDraft] = useState('');
  const [ratingReason, setRatingReason] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [checkrReason, setCheckrReason] = useState('');
  const [quizSaving, setQuizSaving] = useState(false);
  const [quizFeedback, setQuizFeedback] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState('');
  const [adjustmentDraft, setAdjustmentDraft] = useState({
    type: 'manual_debit',
    amount: '',
    reason: '',
  });
  const [stripeLink, setStripeLink] = useState('');
  const [stripeAccountIdDraft, setStripeAccountIdDraft] = useState('');
  const [stripeLinkLoading, setStripeLinkLoading] = useState(false);
  const [stripeFeedback, setStripeFeedback] = useState('');

  useEffect(() => {
    if (did) {
      dispatch(getAdminDriverDetail(did));
      dispatch(getDriverAdjustments(did));
    }
  }, [did, dispatch]);

  useEffect(() => {
    if (!driver) return;
    setRatingDraft((current) => (current === '' ? String(driver.rating ?? 5) : current));
    setStripeAccountIdDraft(driver.stripeAccountID || '');
  }, [driver]);

  const refresh = () => {
    dispatch(getAdminDriverDetail(did));
    dispatch(getDriverAdjustments(did));
  };

  if (loading || !driver) {
    return <div className="text-[#71717A]">Loading driver details…</div>;
  }

  const isSuspended = driver.suspendedUntil && new Date(driver.suspendedUntil) > new Date();

  return (
    <div className="max-w-6xl">
      {/* Header card */}
      <SectionCard
        title={`${driver.firstName} ${driver.lastName}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {driver.email ? (
              <a
                href={`mailto:${driver.email}`}
                className="inline-flex items-center gap-1 text-[#4F46E5] hover:underline"
              >
                {driver.email}
              </a>
            ) : null}
            {driver.phoneNumber ? (
              <a
                href={`tel:${driver.phoneNumber}`}
                className="inline-flex items-center gap-1 text-[#4F46E5] hover:underline"
              >
                {driver.phoneNumber}
              </a>
            ) : null}
            {driver.createdAt ? (
              <span className="text-[#71717A]">
                · Signed up{' '}
                <strong className="text-[#3F3F46]">
                  {new Date(driver.createdAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </strong>
              </span>
            ) : null}
          </span>
        }
        action={
          (() => {
            // Defensive coercion: legacy driver docs may return `isActive`
            // as undefined. The list endpoint treats "unset = active", and
            // the detail endpoint now does the same, but we normalize here
            // too so the toggle never shows a stale "Inactive" if either
            // side is ever out of sync.
            const isActive = driver.isActive !== false
            return (
              <div className="flex items-center gap-3">
                <span className="text-xs text-[#71717A]">
                  {isActive ? 'Active' : 'Inactive'}
                </span>
                <button
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isActive ? 'bg-[#059669]' : 'bg-[#D4D4D8]'
                  }`}
                  onClick={async () => {
                    await dispatch(
                      updateDriverStatus({
                        did,
                        isActive: !isActive,
                        reason: statusReason.trim() || undefined,
                      })
                    );
                    setStatusReason('');
                    refresh();
                  }}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            )
          })()
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs uppercase text-[#71717A] mb-1">Rating</div>
            <RatingBig value={driver.rating} />
          </div>
          <div>
            <div className="text-xs uppercase text-[#71717A] mb-1">Orders</div>
            <div className="text-2xl font-semibold text-[#18181B]">
              {stats?.totalOrders ?? 0}
              <span className="text-sm text-[#71717A] font-normal ml-2">
                {stats?.completedOrders ?? 0} completed
              </span>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-[#71717A] mb-1">Outstanding</div>
            <div className="text-2xl font-semibold text-[#18181B]">
              ${Number(stats?.outstandingDebt ?? 0).toFixed(2)}
            </div>
          </div>
        </div>
        {isSuspended && (
          <div className="mt-4 bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg p-3 text-sm text-[#991B1B]">
            Suspended until {new Date(driver.suspendedUntil).toLocaleString()}. Reason:{' '}
            {driver.suspensionReason || 'unspecified'}.
            <button
              className="ml-3 underline text-[#991B1B]"
              onClick={async () => {
                await dispatch(updateDriverStatus({ did, isActive: true, reason: 'cleared' }));
                refresh();
              }}
            >
              Clear suspension
            </button>
          </div>
        )}
        {driver.eligibility && !driver.eligibility.eligible && (
          <div className="mt-4 flex flex-wrap gap-2">
            {driver.eligibility.reasons.map((r) => (
              <Badge key={r} variant="danger">
                {r.replaceAll('_', ' ')}
              </Badge>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Stripe"
        subtitle="Connect account id and a fresh onboarding link you can copy and send to this concierge."
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-[#71717A] mb-1">Stripe account ID</label>
            <div className="flex flex-wrap gap-2 items-center">
              <input
                readOnly
                value={stripeAccountIdDraft || 'No Stripe account yet'}
                onFocus={(e) => stripeAccountIdDraft && e.target.select()}
                className="flex-1 min-w-[240px] px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm font-mono focus:outline-none focus:border-[#4F46E5] bg-[#FAFAFA]"
              />
              <button
                type="button"
                disabled={!stripeAccountIdDraft}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(stripeAccountIdDraft);
                    setStripeFeedback('Account ID copied');
                  } catch {
                    setStripeFeedback('Could not copy account ID');
                  }
                }}
                className="bg-white border border-[#E4E4E7] text-[#3F3F46] text-sm px-4 py-2 rounded-lg disabled:opacity-50 hover:border-[#A5B4FC]"
              >
                Copy ID
              </button>
            </div>
            <div className="mt-1 text-xs text-[#71717A]">
              {driver.isOnBoarded ? 'Marked onboarded' : 'Not marked onboarded'}
            </div>
          </div>

          <div>
            <label className="block text-xs text-[#71717A] mb-1">Onboarding link</label>
            <div className="flex flex-wrap gap-2 items-center">
              <input
                readOnly
                value={stripeLink || 'Generate a fresh Stripe onboarding link to copy and paste'}
                onFocus={(e) => stripeLink && e.target.select()}
                className="flex-1 min-w-[240px] px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm font-mono focus:outline-none focus:border-[#4F46E5] bg-[#FAFAFA]"
              />
              <button
                type="button"
                disabled={stripeLinkLoading}
                onClick={async () => {
                  setStripeLinkLoading(true);
                  setStripeFeedback('');
                  try {
                    const data = await fetchDriverStripeOnboardingLink(did);
                    setStripeLink(data?.url || '');
                    if (data?.stripeAccountID) setStripeAccountIdDraft(data.stripeAccountID);
                    if (data?.url) {
                      try {
                        await navigator.clipboard.writeText(data.url);
                        setStripeFeedback('Onboarding link copied');
                      } catch {
                        setStripeFeedback('Link ready — select the field to copy');
                      }
                    }
                    refresh();
                  } catch (err) {
                    setStripeFeedback(err?.response?.data?.message || 'Could not create onboarding link');
                  } finally {
                    setStripeLinkLoading(false);
                  }
                }}
                className="bg-[#4F46E5] text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-[#4338CA]"
              >
                {stripeLinkLoading ? 'Generating…' : stripeLink ? 'Refresh & copy link' : 'Generate & copy link'}
              </button>
            </div>
            {stripeFeedback ? (
              <div className="mt-1 text-xs text-[#3F3F46]">{stripeFeedback}</div>
            ) : (
              <div className="mt-1 text-xs text-[#71717A]">
                Stripe onboarding links expire. Generate a new one each time you send it.
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Checkr background-check control */}
      {(() => {
        // Checkr status drives eligibility. Currently supported values are
        // 'clear', 'pending', 'consider', and 'cancelled'. We expose a
        // manual override because the Checkr webhook occasionally fails to
        // update drivers who are cleared via vendor-side manual review, so
        // ops need a way to unstick them.
        const current = driver.checkrVerified || 'pending'
        const toneByStatus = {
          clear: 'success',
          consider: 'warning',
          pending: 'default',
          cancelled: 'danger',
        }
        const labelByStatus = {
          clear: 'Cleared',
          consider: 'Consider',
          pending: 'Pending',
          cancelled: 'Cancelled',
        }
        return (
          <SectionCard
            title="Background Check (Checkr)"
            subtitle="Override the Checkr status manually when the webhook stalls. Changes are audit-logged."
            action={
              <Badge variant={toneByStatus[current] || 'default'} dot>
                {labelByStatus[current] || current}
              </Badge>
            }
          >
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs text-[#71717A] mb-1">
                  New status
                </label>
                <div className="flex flex-wrap gap-2">
                  {['clear', 'pending', 'consider', 'cancelled'].map((s) => {
                    const selected = current === s
                    return (
                      <button
                        key={s}
                        type="button"
                        disabled={selected}
                        onClick={async () => {
                          await dispatch(
                            updateDriverCheckrStatus({
                              did,
                              status: s,
                              reason: checkrReason.trim() || undefined,
                            })
                          )
                          setCheckrReason('')
                          refresh()
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                          selected
                            ? 'bg-[#4F46E5] text-white border-[#4F46E5] cursor-default'
                            : 'bg-white text-[#3F3F46] border-[#E4E4E7] hover:border-[#A5B4FC]'
                        }`}
                      >
                        {labelByStatus[s]}
                      </button>
                    )
                  })}
                </div>
              </div>
              <div className="flex-1 min-w-[240px]">
                <label className="block text-xs text-[#71717A] mb-1">
                  Reason (optional but recommended)
                </label>
                <input
                  value={checkrReason}
                  onChange={(e) => setCheckrReason(e.target.value)}
                  placeholder="e.g. Vendor review completed 2026-04-22"
                  className="w-full px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#4F46E5]"
                />
              </div>
            </div>
          </SectionCard>
        )
      })()}

      {/* Training quiz gate */}
      {(() => {
        // `quizStatus` is a plain Boolean on the Driver doc. The mobile app
        // forces the training/quiz flow whenever it is falsy, so ops need a
        // way to mark it passed (e.g. after verifying training offline) or
        // reset it to make a concierge re-take the quiz.
        const quizPassed = driver.quizStatus === true
        const currentValue = quizPassed ? 'passed' : 'not_completed'
        return (
          <SectionCard
            title="Training Quiz"
            subtitle="Controls whether the concierge app sends this person through the training quiz on next launch."
            action={
              <Badge variant={quizPassed ? 'success' : 'warning'} dot>
                {quizPassed ? 'Passed' : 'Not completed'}
              </Badge>
            }
          >
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label htmlFor="quiz-status-select" className="block text-xs text-[#71717A] mb-1">
                  Quiz status
                </label>
                <select
                  id="quiz-status-select"
                  value={currentValue}
                  disabled={quizSaving}
                  onChange={async (e) => {
                    const next = e.target.value === 'passed'
                    if (next === quizPassed) return
                    setQuizSaving(true)
                    setQuizFeedback('')
                    try {
                      await dispatch(updateDriverQuizStatus({ did, status: next })).unwrap()
                      setQuizFeedback(next ? 'Marked as passed' : 'Reset — quiz required on next app launch')
                      refresh()
                    } catch (err) {
                      setQuizFeedback(err?.message || err?.error || 'Could not update quiz status')
                    } finally {
                      setQuizSaving(false)
                    }
                  }}
                  className="px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm bg-white focus:outline-none focus:border-[#4F46E5] disabled:opacity-50"
                >
                  <option value="passed">Passed</option>
                  <option value="not_completed">Not completed</option>
                </select>
              </div>
              <div className="text-xs text-[#71717A] pb-2">
                {quizSaving
                  ? 'Saving…'
                  : quizFeedback || 'Changes apply immediately; the app re-checks this on next launch.'}
              </div>
            </div>
          </SectionCard>
        )
      })()}

      {/* Rating editor */}
      <SectionCard title="Adjust Rating" subtitle="Rating changes are audit-logged.">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-[#71717A] mb-1">Rating (0.0 - 5.0)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="5"
              value={ratingDraft}
              onChange={(e) => setRatingDraft(e.target.value)}
              className="w-32 px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#4F46E5]"
            />
          </div>
          <div className="flex-1 min-w-[240px]">
            <label className="block text-xs text-[#71717A] mb-1">Reason (required)</label>
            <input
              value={ratingReason}
              onChange={(e) => setRatingReason(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#4F46E5]"
            />
          </div>
          <button
            className="bg-[#4F46E5] text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
            disabled={!ratingReason.trim() || ratingDraft === ''}
            onClick={async () => {
              await dispatch(
                updateDriverRating({
                  did,
                  rating: Number(ratingDraft),
                  reason: ratingReason.trim(),
                })
              );
              setRatingReason('');
              refresh();
            }}
          >
            Save Rating
          </button>
        </div>
      </SectionCard>

      {/* Compliance */}
      <SectionCard title="Compliance" subtitle="Photo ID and selfie must both be approved before a driver can accept new orders.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ComplianceDoc
            label="Photo ID"
            doc={driver.compliance?.photoId}
            uploading={uploadingDoc === 'photoId'}
            onReview={async (body) => {
              await dispatch(reviewDriverCompliance({ did, doc: 'photoId', ...body }));
              refresh();
            }}
            onUpload={async (file) => {
              setUploadingDoc('photoId');
              try {
                await dispatch(uploadDriverCompliance({ did, doc: 'photoId', file })).unwrap();
                refresh();
              } finally {
                setUploadingDoc('');
              }
            }}
          />
          <ComplianceDoc
            label="Selfie"
            doc={driver.compliance?.selfie}
            uploading={uploadingDoc === 'selfie'}
            onReview={async (body) => {
              await dispatch(reviewDriverCompliance({ did, doc: 'selfie', ...body }));
              refresh();
            }}
            onUpload={async (file) => {
              setUploadingDoc('selfie');
              try {
                await dispatch(uploadDriverCompliance({ did, doc: 'selfie', file })).unwrap();
                refresh();
              } finally {
                setUploadingDoc('');
              }
            }}
          />
        </div>
      </SectionCard>

      {/* Adjustments */}
      <SectionCard
        title="Adjustments Ledger"
        subtitle="Cancellation fees, rating penalties, and manual credits/debits."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
          <select
            className="px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm"
            value={adjustmentDraft.type}
            onChange={(e) => setAdjustmentDraft({ ...adjustmentDraft, type: e.target.value })}
          >
            <option value="manual_debit">Manual Debit</option>
            <option value="manual_credit">Manual Credit</option>
          </select>
          <input
            placeholder="Amount (USD)"
            type="number"
            step="0.01"
            min="0.01"
            value={adjustmentDraft.amount}
            onChange={(e) => setAdjustmentDraft({ ...adjustmentDraft, amount: e.target.value })}
            className="px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm"
          />
          <input
            placeholder="Reason"
            value={adjustmentDraft.reason}
            onChange={(e) => setAdjustmentDraft({ ...adjustmentDraft, reason: e.target.value })}
            className="px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm"
          />
        </div>
        <button
          className="bg-[#4F46E5] text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 mb-4"
          disabled={!adjustmentDraft.amount || !adjustmentDraft.reason.trim()}
          onClick={async () => {
            await dispatch(
              createDriverAdjustment({
                did,
                type: adjustmentDraft.type,
                amount: Number(adjustmentDraft.amount),
                reason: adjustmentDraft.reason.trim(),
              })
            );
            setAdjustmentDraft({ type: 'manual_debit', amount: '', reason: '' });
            refresh();
          }}
        >
          Create Adjustment
        </button>

        {adjustmentsLoading && <div className="text-sm text-[#71717A]">Loading…</div>}
        {!adjustmentsLoading && adjustments.length === 0 && (
          <div className="text-sm text-[#71717A]">No adjustments recorded.</div>
        )}
        {adjustments.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-[#71717A] bg-[#FAFAFA]">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Amount</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((a) => (
                  <tr key={a._id} className="border-t border-[#F4F4F5]">
                    <td className="px-3 py-2 text-[#71717A] text-xs">
                      {new Date(a.createdAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-[#18181B]">{a.type.replaceAll('_', ' ')}</td>
                    <td className="px-3 py-2 text-[#18181B]">
                      {a.type === 'rating_penalty' || a.type === 'manual_rating_change'
                        ? `${a.before?.toFixed(1) ?? '?'} → ${a.after?.toFixed(1) ?? '?'}`
                        : `$${Number(a.amount).toFixed(2)}`}
                    </td>
                    <td className="px-3 py-2 text-[#3F3F46] max-w-xs truncate" title={a.reason}>
                      {a.reason || '—'}
                    </td>
                    <td className="px-3 py-2">
                      <Badge
                        variant={
                          a.status === 'pending'
                            ? 'warning'
                            : a.status === 'voided'
                              ? 'default'
                              : 'success'
                        }
                      >
                        {a.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Recent orders */}
      <SectionCard title="Recent Orders" subtitle="Latest 10 orders assigned to this driver.">
        {recentOrders && recentOrders.length > 0 ? (
          <DriverOrdersTable data={recentOrders} />
        ) : (
          <div className="text-sm text-[#71717A]">No orders yet.</div>
        )}
      </SectionCard>
    </div>
  );
};

export default DriverPersonalComponent;
